function debounce(callback, wait = 250) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => callback(...args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.slides = [...this.querySelectorAll('.announcement__text')];
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.activeIndex = 0;
      this.isPaused = false;
      this.timer = null;
      this.animation = null;
      this.handleLayoutChange = debounce(this.start.bind(this));
      this.bindEvents();
      this.moveLinks();
      this.start();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.animation?.cancel();
      window.removeEventListener('resize', this.handleLayoutChange);
      window.removeEventListener('on:breakpoint-change', this.handleLayoutChange);
    }

    bindEvents() {
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      window.addEventListener('resize', this.handleLayoutChange, { passive: true });
      window.addEventListener('on:breakpoint-change', this.handleLayoutChange);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.resume();
      });
    }

    start() {
      clearTimeout(this.timer);
      this.animation?.cancel();
      this.animation = null;
      if (!this.slider || !this.slides.length) return;
      this.showSlide(this.activeIndex);
      this.moveLinks();

      if (window.matchMedia('(max-width: 768px)').matches) {
        this.startMobileMarquee();
      } else {
        this.resetTracks();
        this.scheduleDesktopSlide();
      }
    }

    showSlide(index) {
      this.activeIndex = index % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.activeIndex;
        slide.classList.toggle('is-visible', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    startMobileMarquee() {
      const slide = this.slides[this.activeIndex];
      const track = slide?.querySelector('.rte');
      if (!track) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        track.style.transform = 'none';
        return;
      }

      requestAnimationFrame(() => {
        if (!this.isConnected || this.isPaused) return;
        const containerWidth = this.slider.clientWidth;
        const textWidth = track.scrollWidth;
        const configuredDuration = Number(this.dataset.slideDelay) || 7000;
        const travelDuration = Math.max(configuredDuration, ((containerWidth + textWidth) / 48) * 1000);

        this.animation = track.animate(
          [
            { transform: `translateX(${containerWidth}px)` },
            { transform: `translateX(-${textWidth}px)` }
          ],
          { duration: travelDuration, easing: 'linear', fill: 'forwards' }
        );

        this.animation.finished.then(() => {
          if (!this.isConnected) return;
          this.activeIndex = (this.activeIndex + 1) % this.slides.length;
          this.showSlide(this.activeIndex);
          this.startMobileMarquee();
        }).catch(() => {});
      });
    }

    scheduleDesktopSlide() {
      if (this.isPaused || this.slides.length < 2) return;
      const duration = Number(this.dataset.slideDelay) || 7000;
      this.timer = setTimeout(() => {
        this.activeIndex = (this.activeIndex + 1) % this.slides.length;
        this.showSlide(this.activeIndex);
        const slide = this.slides[this.activeIndex];
        this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        this.scheduleDesktopSlide();
      }, duration);
    }

    resetTracks() {
      this.slides.forEach((slide) => {
        const track = slide.querySelector('.rte');
        if (track) track.style.transform = '';
      });
    }

    pause() {
      this.isPaused = true;
      clearTimeout(this.timer);
      this.animation?.pause();
    }

    resume() {
      if (!this.isPaused) return;
      this.isPaused = false;
      if (this.animation) this.animation.play();
      else this.start();
    }

    moveLinks() {
      if (!this.links.length || !this.menu) return;
      const mobileLinks = document.querySelector('.mob__announcement-links');
      if (window.innerWidth >= 769) {
        mobileLinks?.remove();
        return;
      }
      if (mobileLinks) return;

      const nav = document.createElement('nav');
      nav.className = 'mob__announcement-links';
      const list = document.createElement('ul');
      list.className = 'secondary-nav';
      this.links.forEach((link) => {
        const item = document.createElement('li');
        const copy = link.cloneNode(true);
        copy.className = 'secondary-nav__item';
        item.appendChild(copy);
        list.appendChild(item);
      });
      nav.appendChild(list);
      this.menu.appendChild(nav);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
